import { Timestamp } from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ADMIN_PASSPHRASE, ADMIN_ROUTE_PATH } from "../adminConfig";
import Footer from "../components/Footer";
import SiteHeader from "../components/SiteHeader";
import {
  archiveLab,
  backfillSubscribersFromLegacyRegistrations,
  deleteRegistration,
  getAllLabs,
  getAllRegistrations,
  getLabById,
  getSubscriberByEmail,
  getUpcomingLabs,
  normalizeEmail,
  setSubscriberActiveByEmail,
  updateRegistration,
  upsertLab,
  upsertLabDeliveryRegistration,
  upsertSubscriber,
} from "../firebase";
import {
  createEditorId,
  normalizeLabActivities,
  normalizeLabPolls,
} from "../utils/labContent";
import { normalizeStateToAbbreviation } from "../utils/usStates";
import { formatLabDate, formatLabTimeRange, toDateValue } from "../utils/datetime";

const SESSION_KEY = "school_leader_ai_lab_admin_unlock";
const REGISTRATION_FETCH_LIMIT = 5000;
const GROUP_PREVIEW_LIMIT = 6;

const emptyForm = {
  id: "",
  status: "draft",
  title: "",
  dateTimeLocal: "",
  durationMinutes: "60",
  shortDescription: "",
  longDescription: "",
  zoomLink: "",
  youtubeId: "",
  resourcesText: "",
  polls: [],
  activities: [],
};

const defaultRegistrationFilters = {
  search: "",
  dateFrom: "",
  dateTo: "",
  association: "",
  subscriptionStatus: "",
  state: "",
  role: "",
  labId: "",
  sourceType: "",
  sourceSlug: "",
  sort: "newest",
};

const REGISTRATION_COLUMNS = [
  { id: "createdAt", label: "Registered" },
  { id: "firstName", label: "First name" },
  { id: "email", label: "Email" },
  { id: "role", label: "Role" },
  { id: "state", label: "State" },
  { id: "subscriberActive", label: "Subscription" },
  { id: "unsubscribedAt", label: "Unsubscribed" },
  { id: "association", label: "Association" },
  { id: "sourceType", label: "Source type" },
  { id: "sourceSlug", label: "Source slug" },
  { id: "lab", label: "Lab" },
  { id: "tags", label: "Tags" },
  { id: "notes", label: "Notes" },
];

const DEFAULT_VISIBLE_COLUMNS = {
  createdAt: true,
  firstName: true,
  email: true,
  role: true,
  state: true,
  subscriberActive: true,
  unsubscribedAt: true,
  association: true,
  sourceType: true,
  sourceSlug: true,
  lab: true,
  tags: true,
  notes: false,
};

const BULK_IMPORT_REQUIRED_FIELDS = ["firstName", "email", "role", "state"];
const BULK_IMPORT_DEFAULT_SOURCE = {
  sourceType: "admin-bulk",
  sourceSlug: "vault-bulk-add",
  sourceName: "Admin bulk add",
};

const BULK_IMPORT_HEADER_ALIASES = {
  firstname: "firstName",
  first: "firstName",
  email: "email",
  role: "role",
  state: "state",
  sourcetype: "sourceType",
  sourceslug: "sourceSlug",
  sourcename: "sourceName",
};

const BULK_IMPORT_TEMPLATE_HEADERS = [
  "firstName",
  "email",
  "role",
  "state",
  "sourceType",
  "sourceSlug",
  "sourceName",
];

const BULK_IMPORT_SAMPLE_ROW = [
  "Jordan",
  "jordan@examplek12.org",
  "Principal",
  "MN",
  "association",
  "new-hampshire-association-of-school-principals",
  "New Hampshire Association of School Principals",
];

const normalizeBulkHeader = (value) =>
  `${value || ""}`.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && text[index + 1] === "\n") {
        index += 1;
      }

      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (inQuotes) {
    return {
      rows: [],
      error: "CSV has an unclosed quote. Please fix the file and try again.",
    };
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return { rows, error: "" };
};

const parseBulkCsv = (csvText) => {
  const normalizedText = `${csvText || ""}`.replace(/^\uFEFF/, "");
  const { rows, error } = parseCsvRows(normalizedText);

  if (error) {
    return {
      rows: [],
      columns: [],
      missingRequired: [...BULK_IMPORT_REQUIRED_FIELDS],
      error,
    };
  }

  const meaningfulRows = rows.filter((cells) =>
    cells.some((value) => `${value || ""}`.trim())
  );

  if (meaningfulRows.length === 0) {
    return {
      rows: [],
      columns: [],
      missingRequired: [...BULK_IMPORT_REQUIRED_FIELDS],
      error: "CSV is empty.",
    };
  }

  const headerCells = meaningfulRows[0].map((value) => `${value || ""}`.trim());
  const columns = headerCells.map((headerCell) => {
    const normalizedHeader = normalizeBulkHeader(headerCell);
    return BULK_IMPORT_HEADER_ALIASES[normalizedHeader] || null;
  });
  const availableColumns = new Set(columns.filter(Boolean));
  const missingRequired = BULK_IMPORT_REQUIRED_FIELDS.filter(
    (requiredField) => !availableColumns.has(requiredField)
  );

  return {
    rows: meaningfulRows.slice(1),
    columns,
    missingRequired,
    error: "",
  };
};

const normalizeLabId = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatDateForInput = (value) => {
  const date = toDateValue(value);
  if (!date) {
    return "";
  }

  const pad = (num) => `${num}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const serializeResources = (resources) => {
  if (!Array.isArray(resources) || resources.length === 0) {
    return "";
  }

  return resources.map((resource) => `${resource.label}|${resource.url}`).join("\n");
};

const parseResources = (resourcesText) => {
  return resourcesText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [labelPart, ...urlParts] = line.split("|");
      const label = (labelPart || "").trim();
      const url = urlParts.join("|").trim();

      if (!url) {
        return {
          label,
          url: label,
        };
      }

      return {
        label: label || url,
        url,
      };
    })
    .filter((resource) => resource.url);
};

const normalizeUrlValue = (value) => {
  const raw = `${value || ""}`.trim();
  if (!raw) {
    return "";
  }

  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
  const candidate = hasScheme ? raw : `https://${raw.replace(/^\/+/, "")}`;

  try {
    const parsed = new URL(candidate);
    if (!/^https?:$/i.test(parsed.protocol)) {
      return null;
    }
    return parsed.toString();
  } catch (error) {
    return null;
  }
};

const extractYouTubeId = (value) => {
  const raw = `${value || ""}`.trim();
  if (!raw) {
    return "";
  }

  const plainIdMatch = raw.match(/^[a-zA-Z0-9_-]{6,}$/);
  if (plainIdMatch) {
    return plainIdMatch[0];
  }

  try {
    const parsed = new URL(raw);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.replace(/\//g, "").trim();
    }

    if (parsed.hostname.includes("youtube.com")) {
      const fromQuery = parsed.searchParams.get("v");
      if (fromQuery) {
        return fromQuery.trim();
      }

      const embedPathMatch = parsed.pathname.match(/\/embed\/([^/?]+)/i);
      if (embedPathMatch) {
        return embedPathMatch[1].trim();
      }
    }
  } catch (error) {
    return raw;
  }

  return raw;
};

const mapLabToForm = (lab) => ({
  id: lab.id || "",
  status: lab.status || "published",
  title: lab.title || "",
  dateTimeLocal: formatDateForInput(lab.dateTime),
  durationMinutes: `${lab.durationMinutes || 60}`,
  shortDescription: lab.shortDescription || "",
  longDescription: lab.longDescription || "",
  zoomLink: lab.zoomLink || "",
  youtubeId: lab.youtubeId || "",
  resourcesText: serializeResources(lab.resources),
  polls: normalizeLabPolls(lab.polls),
  activities: normalizeLabActivities(lab.activities),
});

const moveListItem = (items, fromIndex, delta) => {
  if (!Array.isArray(items) || items.length === 0) {
    return items || [];
  }

  const targetIndex = fromIndex + delta;
  if (
    fromIndex < 0 ||
    fromIndex >= items.length ||
    targetIndex < 0 ||
    targetIndex >= items.length
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
};

const statusLabel = (status) => {
  if (status === "archived") {
    return "Archived";
  }

  if (status === "draft") {
    return "Draft";
  }

  return "Published";
};

const parseTags = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((tag) => `${tag || ""}`.trim())
          .filter(Boolean)
      )
    );
  }

  return Array.from(
    new Set(
      `${value || ""}`
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
    )
  );
};

const mapRegistrationForUi = (registration) => ({
  ...registration,
  subscriberId: registration.subscriberId || normalizeEmail(registration.email || ""),
  deliveryActive: registration.deliveryActive !== false,
  tags: parseTags(registration.tags),
  notes: `${registration.notes || ""}`,
  unsubscribedAt: registration.unsubscribedAt || null,
});

const formatRegistrationDateTime = (value) => {
  const date = toDateValue(value);
  if (!date) {
    return "Unknown";
  }

  return `${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Chicago",
  }).format(date)} CT`;
};

const getRegistrationTimestampMs = (registration) => {
  const date = toDateValue(registration.createdAt);
  return date ? date.getTime() : 0;
};

const toDateStartOfDay = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateEndOfDay = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toRegistrationEditor = (registration) => ({
  firstName: registration.firstName || "",
  email: registration.email || "",
  role: registration.role || "",
  state: registration.state || "",
  labId: registration.labId || "",
  sourceType: registration.sourceType || "public",
  sourceSlug: registration.sourceSlug || "",
  sourceName: registration.sourceName || "",
  notes: registration.notes || "",
  tagsText: parseTags(registration.tags).join(", "),
});

const getTopGroups = (registrations, getLabel, maxItems = GROUP_PREVIEW_LIMIT) => {
  const counters = registrations.reduce((accumulator, registration) => {
    const label = getLabel(registration);
    accumulator.set(label, (accumulator.get(label) || 0) + 1);
    return accumulator;
  }, new Map());

  return Array.from(counters.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, maxItems)
    .map(([label, count]) => ({ label, count }));
};

const getTrend = (registrations, days) => {
  const windowMs = days * 24 * 60 * 60 * 1000;
  const nowMs = Date.now();
  const currentWindowStart = nowMs - windowMs;
  const previousWindowStart = nowMs - windowMs * 2;

  let current = 0;
  let previous = 0;

  registrations.forEach((registration) => {
    const timestamp = getRegistrationTimestampMs(registration);
    if (!timestamp) {
      return;
    }

    if (timestamp >= currentWindowStart && timestamp <= nowMs) {
      current += 1;
      return;
    }

    if (timestamp >= previousWindowStart && timestamp < currentWindowStart) {
      previous += 1;
    }
  });

  return {
    current,
    previous,
    delta: current - previous,
  };
};

const summarizeTrendDelta = (trend) => {
  if (trend.delta === 0) {
    return "No change vs previous period";
  }

  if (trend.delta > 0) {
    return `+${trend.delta} vs previous period`;
  }

  return `${trend.delta} vs previous period`;
};

const csvEscape = (value) => {
  const normalized = `${value ?? ""}`;
  if (!normalized.includes(",") && !normalized.includes('"') && !normalized.includes("\n")) {
    return normalized;
  }

  return `"${normalized.replace(/"/g, '""')}"`;
};

const downloadCsv = (rows, fileName) => {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const objectUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const notePreview = (notes) => {
  const value = `${notes || ""}`.trim();
  if (!value) {
    return "-";
  }

  if (value.length <= 48) {
    return value;
  }

  return `${value.slice(0, 45)}...`;
};

function AdminPage() {
  const [isUnlocked, setIsUnlocked] = useState(() => {
    return sessionStorage.getItem(SESSION_KEY) === "true";
  });
  const [passphrase, setPassphrase] = useState("");
  const [unlockError, setUnlockError] = useState("");

  const [labs, setLabs] = useState([]);
  const [isLoadingLabs, setIsLoadingLabs] = useState(true);
  const [labsLoadError, setLabsLoadError] = useState("");

  const [formData, setFormData] = useState(() => ({
    ...emptyForm,
    polls: [],
    activities: [],
  }));
  const [activeLabId, setActiveLabId] = useState("");
  const [isSavingLab, setIsSavingLab] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: "", message: "" });

  const [registrations, setRegistrations] = useState([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(true);
  const [registrationsLoadError, setRegistrationsLoadError] = useState("");
  const [registrationFilters, setRegistrationFilters] = useState(defaultRegistrationFilters);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState("");
  const [registrationMode, setRegistrationMode] = useState("view");
  const [registrationEditor, setRegistrationEditor] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState({ type: "", message: "" });
  const [isSavingRegistration, setIsSavingRegistration] = useState(false);
  const [resendingRegistrationId, setResendingRegistrationId] = useState("");
  const [deletingRegistrationId, setDeletingRegistrationId] = useState("");
  const [togglingSubscriberEmail, setTogglingSubscriberEmail] = useState("");
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [bulkImportCsvText, setBulkImportCsvText] = useState("");
  const [bulkImportFileName, setBulkImportFileName] = useState("");
  const [bulkImportLabId, setBulkImportLabId] = useState("");
  const [bulkImportSendEmails, setBulkImportSendEmails] = useState(false);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportStatus, setBulkImportStatus] = useState({ type: "", message: "" });
  const [bulkImportReport, setBulkImportReport] = useState(null);
  const topTableScrollRef = useRef(null);
  const topTableScrollInnerRef = useRef(null);
  const bottomTableScrollRef = useRef(null);
  const isSyncingTableScrollRef = useRef(false);

  const activeLab = useMemo(
    () => labs.find((lab) => lab.id === activeLabId) || null,
    [activeLabId, labs]
  );

  const selectedRegistration = useMemo(
    () => registrations.find((registration) => registration.id === selectedRegistrationId) || null,
    [registrations, selectedRegistrationId]
  );

  const labTitleById = useMemo(() => {
    return labs.reduce((accumulator, lab) => {
      accumulator[lab.id] = lab.title || lab.id;
      return accumulator;
    }, {});
  }, [labs]);

  const getLabLabel = (labId) => {
    if (!labId) {
      return "General monthly lab";
    }

    return labTitleById[labId] || labId;
  };

  const loadLabs = async () => {
    setIsLoadingLabs(true);
    setLabsLoadError("");

    try {
      const response = await getAllLabs(400);
      setLabs(response);
    } catch (error) {
      console.error("Failed to load labs for admin:", error);
      setLabsLoadError("Could not load lab content. Please refresh and try again.");
    } finally {
      setIsLoadingLabs(false);
    }
  };

  const loadRegistrations = async () => {
    setIsLoadingRegistrations(true);
    setRegistrationsLoadError("");

    try {
      const response = await getAllRegistrations(REGISTRATION_FETCH_LIMIT);
      const registrationsForUi = response.map(mapRegistrationForUi);
      const unsubscribedSubscriberIds = Array.from(
        new Set(
          registrationsForUi
            .filter((registration) => registration.deliveryActive === false)
            .map((registration) => registration.subscriberId || normalizeEmail(registration.email))
            .filter(Boolean)
        )
      );

      let unsubscribedAtBySubscriberId = new Map();
      if (unsubscribedSubscriberIds.length > 0) {
        const unsubscribedSubscribers = await Promise.all(
          unsubscribedSubscriberIds.map((subscriberId) =>
            getSubscriberByEmail(subscriberId).catch((error) => {
              console.error("Failed to load subscriber details for admin:", error);
              return null;
            })
          )
        );
        unsubscribedAtBySubscriberId = new Map(
          unsubscribedSubscribers
            .filter(Boolean)
            .map((subscriber) => [
              subscriber.id || normalizeEmail(subscriber.email || ""),
              subscriber.unsubscribedAt || null,
            ])
        );
      }

      setRegistrations(
        registrationsForUi.map((registration) => ({
          ...registration,
          unsubscribedAt:
            registration.unsubscribedAt ||
            unsubscribedAtBySubscriberId.get(registration.subscriberId) ||
            null,
        }))
      );
    } catch (error) {
      console.error("Failed to load registrations for admin:", error);
      setRegistrationsLoadError(
        "Could not load registrations. Please refresh and try again."
      );
    } finally {
      setIsLoadingRegistrations(false);
    }
  };

  useEffect(() => {
    if (!isUnlocked) {
      return;
    }

    loadLabs();
    loadRegistrations();
  }, [isUnlocked]);

  useEffect(() => {
    if (!selectedRegistrationId) {
      return;
    }

    const latestSelected = registrations.find(
      (registration) => registration.id === selectedRegistrationId
    );

    if (!latestSelected) {
      setSelectedRegistrationId("");
      setRegistrationEditor(null);
      setRegistrationMode("view");
      return;
    }

    if (registrationMode === "view") {
      setRegistrationEditor(toRegistrationEditor(latestSelected));
    }
  }, [registrations, selectedRegistrationId, registrationMode]);

  useEffect(() => {
    if (bulkImportLabId) {
      return;
    }

    const preferredLab = labs.find((lab) => lab.status === "published") || labs[0];
    if (preferredLab?.id) {
      setBulkImportLabId(preferredLab.id);
    }
  }, [bulkImportLabId, labs]);

  const handleUnlock = (event) => {
    event.preventDefault();

    if (passphrase === ADMIN_PASSPHRASE) {
      sessionStorage.setItem(SESSION_KEY, "true");
      setIsUnlocked(true);
      setUnlockError("");
      setPassphrase("");
      return;
    }

    setUnlockError("Incorrect passphrase.");
  };

  const handleLabFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleAddPoll = () => {
    setFormData((previous) => ({
      ...previous,
      polls: [
        ...(Array.isArray(previous.polls) ? previous.polls : []),
        { id: createEditorId("poll"), question: "" },
      ],
    }));
  };

  const handlePollChange = (pollIndex, nextQuestion) => {
    setFormData((previous) => ({
      ...previous,
      polls: (previous.polls || []).map((poll, index) =>
        index === pollIndex ? { ...poll, question: nextQuestion } : poll
      ),
    }));
  };

  const handleMovePoll = (pollIndex, delta) => {
    setFormData((previous) => ({
      ...previous,
      polls: moveListItem(previous.polls || [], pollIndex, delta),
    }));
  };

  const handleRemovePoll = (pollIndex) => {
    setFormData((previous) => ({
      ...previous,
      polls: (previous.polls || []).filter((_, index) => index !== pollIndex),
    }));
  };

  const handleAddActivity = () => {
    setFormData((previous) => ({
      ...previous,
      activities: [
        ...(Array.isArray(previous.activities) ? previous.activities : []),
        {
          id: createEditorId("activity"),
          title: "",
          links: [],
          steps: [],
        },
      ],
    }));
  };

  const handleActivityTitleChange = (activityIndex, nextTitle) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex ? { ...activity, title: nextTitle } : activity
      ),
    }));
  };

  const handleMoveActivity = (activityIndex, delta) => {
    setFormData((previous) => ({
      ...previous,
      activities: moveListItem(previous.activities || [], activityIndex, delta),
    }));
  };

  const handleRemoveActivity = (activityIndex) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).filter((_, index) => index !== activityIndex),
    }));
  };

  const handleAddActivityLink = (activityIndex) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              links: [
                ...(Array.isArray(activity.links) ? activity.links : []),
                { label: "", url: "" },
              ],
            }
          : activity
      ),
    }));
  };

  const handleActivityLinkChange = (activityIndex, linkIndex, field, nextValue) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) => {
        if (index !== activityIndex) {
          return activity;
        }

        const nextLinks = (activity.links || []).map((link, currentLinkIndex) =>
          currentLinkIndex === linkIndex ? { ...link, [field]: nextValue } : link
        );

        return {
          ...activity,
          links: nextLinks,
        };
      }),
    }));
  };

  const handleRemoveActivityLink = (activityIndex, linkIndex) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              links: (activity.links || []).filter(
                (_, currentLinkIndex) => currentLinkIndex !== linkIndex
              ),
            }
          : activity
      ),
    }));
  };

  const handleAddActivityStep = (activityIndex) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              steps: [
                ...(Array.isArray(activity.steps) ? activity.steps : []),
                { id: createEditorId("step"), text: "", prompt: "" },
              ],
            }
          : activity
      ),
    }));
  };

  const handleActivityStepChange = (activityIndex, stepIndex, field, nextValue) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) => {
        if (index !== activityIndex) {
          return activity;
        }

        return {
          ...activity,
          steps: (activity.steps || []).map((step, currentStepIndex) =>
            currentStepIndex === stepIndex ? { ...step, [field]: nextValue } : step
          ),
        };
      }),
    }));
  };

  const handleMoveActivityStep = (activityIndex, stepIndex, delta) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              steps: moveListItem(activity.steps || [], stepIndex, delta),
            }
          : activity
      ),
    }));
  };

  const handleRemoveActivityStep = (activityIndex, stepIndex) => {
    setFormData((previous) => ({
      ...previous,
      activities: (previous.activities || []).map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              steps: (activity.steps || []).filter(
                (_, currentStepIndex) => currentStepIndex !== stepIndex
              ),
            }
          : activity
      ),
    }));
  };

  const handleIdBlur = () => {
    setFormData((previous) => ({
      ...previous,
      id: normalizeLabId(previous.id),
    }));
  };

  const handleGenerateId = () => {
    setFormData((previous) => ({
      ...previous,
      id: normalizeLabId(previous.title),
    }));
  };

  const handleStartNew = () => {
    setActiveLabId("");
    setFormData({
      ...emptyForm,
      polls: [],
      activities: [],
    });
    setSaveStatus({ type: "", message: "" });
  };

  const handleEditLab = (lab) => {
    setActiveLabId(lab.id);
    setFormData(mapLabToForm(lab));
    setSaveStatus({ type: "", message: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDuplicateLab = (lab) => {
    const nextId = `${normalizeLabId(lab.id)}-copy`;

    setActiveLabId("");
    setFormData({
      ...mapLabToForm(lab),
      id: nextId,
      title: `${lab.title} (Copy)`,
      status: "draft",
    });
    setSaveStatus({
      type: "success",
      message:
        "Duplicated into a new draft. Update details, then click Save lab.",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleArchiveActive = async () => {
    if (!activeLabId) {
      return;
    }

    const approved = window.confirm(
      "Archive this lab? It will be hidden from public pages but remain in admin."
    );

    if (!approved) {
      return;
    }

    try {
      await archiveLab(activeLabId);
      await loadLabs();
      setSaveStatus({ type: "success", message: "Lab archived." });
      if (activeLab) {
        setFormData((previous) => ({
          ...previous,
          status: "archived",
        }));
      }
    } catch (error) {
      console.error("Failed to archive lab:", error);
      setSaveStatus({
        type: "error",
        message: "Could not archive this lab. Please try again.",
      });
    }
  };

  const handleSaveLab = async (event) => {
    event.preventDefault();
    setSaveStatus({ type: "", message: "" });

    const normalizedId = normalizeLabId(formData.id);
    const title = formData.title.trim();
    const date = new Date(formData.dateTimeLocal);
    const shortDescription = formData.shortDescription.trim();
    const longDescription = formData.longDescription.trim();
    const durationMinutes = Number(formData.durationMinutes || 60);
    const normalizedZoomLink = normalizeUrlValue(formData.zoomLink);
    const normalizedYouTubeId = extractYouTubeId(formData.youtubeId);

    const parsedResources = parseResources(formData.resourcesText);
    const normalizedResources = [];
    for (let index = 0; index < parsedResources.length; index += 1) {
      const resource = parsedResources[index];
      const normalizedUrl = normalizeUrlValue(resource.url);
      if (!normalizedUrl) {
        setSaveStatus({
          type: "error",
          message: `Resource ${index + 1} has an invalid URL. Use a full link or domain (example.com).`,
        });
        return;
      }

      normalizedResources.push({
        label: resource.label || normalizedUrl,
        url: normalizedUrl,
      });
    }

    const normalizedActivities = normalizeLabActivities(formData.activities).map((activity) => ({
      ...activity,
      links: activity.links.map((link) => ({ ...link })),
    }));

    for (let activityIndex = 0; activityIndex < normalizedActivities.length; activityIndex += 1) {
      const activity = normalizedActivities[activityIndex];
      for (let linkIndex = 0; linkIndex < activity.links.length; linkIndex += 1) {
        const link = activity.links[linkIndex];
        const normalizedLinkUrl = normalizeUrlValue(link.url);
        if (!normalizedLinkUrl) {
          setSaveStatus({
            type: "error",
            message: `Activity ${activityIndex + 1}, link ${linkIndex + 1} has an invalid URL.`,
          });
          return;
        }

        activity.links[linkIndex] = {
          ...link,
          url: normalizedLinkUrl,
          label: (link.label || normalizedLinkUrl).trim(),
        };
      }
    }

    if (!normalizedId) {
      setSaveStatus({ type: "error", message: "Lab ID is required." });
      return;
    }

    if (!title) {
      setSaveStatus({ type: "error", message: "Title is required." });
      return;
    }

    if (Number.isNaN(date.getTime())) {
      setSaveStatus({
        type: "error",
        message: "Please choose a valid date and time.",
      });
      return;
    }

    if (!durationMinutes || durationMinutes < 15) {
      setSaveStatus({
        type: "error",
        message: "Duration must be at least 15 minutes.",
      });
      return;
    }

    if (!shortDescription) {
      setSaveStatus({
        type: "error",
        message: "Short description is required.",
      });
      return;
    }

    if (!longDescription) {
      setSaveStatus({
        type: "error",
        message: "Long description is required.",
      });
      return;
    }

    if (!normalizedZoomLink) {
      setSaveStatus({
        type: "error",
        message: "Zoom link is required and must be a valid URL.",
      });
      return;
    }

    setIsSavingLab(true);

    try {
      const payload = {
        id: normalizedId,
        status: formData.status,
        title,
        dateTime: Timestamp.fromDate(date),
        durationMinutes,
        shortDescription,
        longDescription,
        zoomLink: normalizedZoomLink,
        youtubeId: normalizedYouTubeId,
        resources: normalizedResources,
        polls: normalizeLabPolls(formData.polls),
        activities: normalizedActivities,
        createdAt: activeLab?.createdAt || Timestamp.now(),
      };

      await upsertLab(payload);
      await loadLabs();

      setActiveLabId(payload.id);
      setFormData((previous) => ({
        ...previous,
        id: payload.id,
        title: payload.title,
        durationMinutes: `${payload.durationMinutes}`,
        shortDescription: payload.shortDescription,
        longDescription: payload.longDescription,
        zoomLink: payload.zoomLink,
        youtubeId: payload.youtubeId,
        resourcesText: serializeResources(payload.resources),
        polls: payload.polls,
        activities: payload.activities,
      }));
      setSaveStatus({ type: "success", message: "Lab content saved." });
    } catch (error) {
      console.error("Failed to save lab:", error);
      setSaveStatus({
        type: "error",
        message:
          error?.message && `${error.message}`.length < 180
            ? `Could not save: ${error.message}`
            : "Could not save lab content. Please try again.",
      });
    } finally {
      setIsSavingLab(false);
    }
  };

  const handleRegistrationFilterChange = (event) => {
    const { name, value } = event.target;
    setRegistrationFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleClearRegistrationFilters = () => {
    setRegistrationFilters(defaultRegistrationFilters);
  };

  const handleToggleColumn = (columnId) => {
    setVisibleColumns((previous) => {
      const visibleCount = Object.values(previous).filter(Boolean).length;
      if (previous[columnId] && visibleCount === 1) {
        return previous;
      }

      return {
        ...previous,
        [columnId]: !previous[columnId],
      };
    });
  };

  const openRegistration = (registration, mode = "view") => {
    setSelectedRegistrationId(registration.id);
    setRegistrationMode(mode);
    setRegistrationEditor(toRegistrationEditor(registration));
    setRegistrationStatus({ type: "", message: "" });
  };

  const closeRegistrationPanel = () => {
    setSelectedRegistrationId("");
    setRegistrationMode("view");
    setRegistrationEditor(null);
  };

  const handleRegistrationEditorChange = (event) => {
    const { name, value } = event.target;
    setRegistrationEditor((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSaveRegistration = async (event) => {
    event.preventDefault();
    if (!selectedRegistration || !registrationEditor) {
      return;
    }

    const firstName = registrationEditor.firstName.trim();
    const email = registrationEditor.email.trim().toLowerCase();
    const role = registrationEditor.role.trim();
    const state = normalizeStateToAbbreviation(registrationEditor.state);
    const sourceType = registrationEditor.sourceType.trim() || "public";
    const sourceSlug =
      registrationEditor.sourceSlug.trim() ||
      (sourceType === "association" ? "association-landing" : "main-hub");
    const sourceName =
      registrationEditor.sourceName.trim() ||
      (sourceType === "association" ? "Association" : "AI Lab Hub");
    const notes = registrationEditor.notes.trim();
    const tags = parseTags(registrationEditor.tagsText);
    const normalizedSubscriberId = normalizeEmail(email);

    if (!firstName || !email) {
      setRegistrationStatus({
        type: "error",
        message: "First name and email are required.",
      });
      return;
    }

    setIsSavingRegistration(true);
    setRegistrationStatus({ type: "", message: "" });

    const payload = {
      firstName,
      email,
      role,
      state,
      labId: registrationEditor.labId || null,
      sourceType,
      sourceSlug,
      sourceName,
      subscriberId: normalizedSubscriberId,
      deliveryVersion: selectedRegistration.deliveryVersion || 2,
      deliveryActive: selectedRegistration.deliveryActive !== false,
      notes,
      tags,
    };

    try {
      await updateRegistration(selectedRegistration.id, payload);
      await upsertSubscriber({
        firstName,
        email,
        role,
        state,
        sourceType,
        sourceSlug,
        sourceName,
        active: selectedRegistration.deliveryActive !== false,
      });

      setRegistrations((previous) =>
        previous.map((registration) =>
          registration.id === selectedRegistration.id
            ? mapRegistrationForUi({
                ...registration,
                ...payload,
              })
            : registration
        )
      );

      setRegistrationMode("view");
      setRegistrationEditor({
        ...registrationEditor,
        firstName,
        email,
        role,
        state,
        sourceType,
        sourceSlug,
        sourceName,
        notes,
        tagsText: tags.join(", "),
      });
      setRegistrationStatus({
        type: "success",
        message: "Registration updated.",
      });
    } catch (error) {
      console.error("Failed to update registration:", error);
      setRegistrationStatus({
        type: "error",
        message: "Could not update this registration. Please try again.",
      });
    } finally {
      setIsSavingRegistration(false);
    }
  };

  const handleDeleteRegistration = async (registration) => {
    const approved = window.confirm(
      `Delete registration for ${registration.firstName || registration.email}? This cannot be undone.`
    );

    if (!approved) {
      return;
    }

    setDeletingRegistrationId(registration.id);
    setRegistrationStatus({ type: "", message: "" });

    try {
      await deleteRegistration(registration.id);
      setRegistrations((previous) =>
        previous.filter((item) => item.id !== registration.id)
      );

      if (selectedRegistrationId === registration.id) {
        closeRegistrationPanel();
      }

      setRegistrationStatus({
        type: "success",
        message: "Registration deleted.",
      });
    } catch (error) {
      console.error("Failed to delete registration:", error);
      setRegistrationStatus({
        type: "error",
        message: "Could not delete this registration. Please try again.",
      });
    } finally {
      setDeletingRegistrationId("");
    }
  };

  const handleResendConfirmation = async (registration) => {
    if (!registration.email) {
      setRegistrationStatus({
        type: "error",
        message: "This registration is missing an email address.",
      });
      return;
    }

    setResendingRegistrationId(registration.id);
    setRegistrationStatus({ type: "", message: "" });

    try {
      let targetLab = null;

      if (registration.labId) {
        targetLab = await getLabById(registration.labId, {
          includeUnpublished: true,
        });
      }

      if (!targetLab) {
        const nextUpcoming = await getUpcomingLabs(1);
        targetLab = nextUpcoming[0] || null;
      }

      if (!targetLab) {
        setRegistrationStatus({
          type: "error",
          message: "No upcoming lab found. Publish a lab first, then resend.",
        });
        return;
      }

      const resentAt = Timestamp.now();
      const resendToken = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      let queuedRegistrationId = registration.id;

      if (registration.labId === targetLab.id) {
        await updateRegistration(registration.id, {
          confirmationRequestedAt: resentAt,
          confirmationRequestToken: resendToken,
          lastConfirmationResentAt: resentAt,
          confirmationError: "",
        });
      } else {
        const subscriber = await upsertSubscriber({
          firstName: registration.firstName || "",
          email: registration.email,
          role: registration.role || "",
          state: registration.state || "",
          sourceType: registration.sourceType || "public",
          sourceSlug: registration.sourceSlug || "main-hub",
          sourceName: registration.sourceName || "AI Lab Hub",
          active: registration.deliveryActive !== false,
        });

        const queuedDelivery = await upsertLabDeliveryRegistration({
          firstName: subscriber.firstName || registration.firstName || "",
          email: subscriber.email || registration.email,
          role: subscriber.role || registration.role || "",
          state: subscriber.state || registration.state || "",
          labId: targetLab.id,
          sourceType: subscriber.sourceType || registration.sourceType || "public",
          sourceSlug: subscriber.sourceSlug || registration.sourceSlug || "main-hub",
          sourceName: subscriber.sourceName || registration.sourceName || "AI Lab Hub",
          requestConfirmation: true,
        });

        if (queuedDelivery.registrationId) {
          queuedRegistrationId = queuedDelivery.registrationId;
          await updateRegistration(queuedDelivery.registrationId, {
            lastConfirmationResentAt: resentAt,
            confirmationError: "",
          });
        }
      }

      setRegistrations((previous) =>
        previous.map((item) =>
          item.id === queuedRegistrationId
            ? {
                ...item,
                lastConfirmationResentAt: resentAt,
                confirmationError: "",
              }
            : item
        )
      );

      await loadRegistrations();

      setRegistrationStatus({
        type: "success",
        message: `Confirmation email queued for ${registration.email}.`,
      });
    } catch (error) {
      console.error("Failed to resend confirmation email:", error);
      setRegistrationStatus({
        type: "error",
        message: "Could not queue confirmation resend. Please try again.",
      });
    } finally {
      setResendingRegistrationId("");
    }
  };

  const handleToggleSubscriberActive = async (registration) => {
    const normalizedSubscriberEmail =
      registration.subscriberId || normalizeEmail(registration.email || "");

    if (!normalizedSubscriberEmail) {
      setRegistrationStatus({
        type: "error",
        message: "This registration is missing an email address.",
      });
      return;
    }

    const currentActive = registration.deliveryActive !== false;
    const nextActive = !currentActive;
    const actionLabel = nextActive ? "reactivate" : "unsubscribe";
    const approved = window.confirm(
      `Are you sure you want to ${actionLabel} ${normalizedSubscriberEmail}?`
    );

    if (!approved) {
      return;
    }

    setTogglingSubscriberEmail(normalizedSubscriberEmail);
    setRegistrationStatus({ type: "", message: "" });

    try {
      const statusChangedAt = Timestamp.now();
      const result = await setSubscriberActiveByEmail({
        email: normalizedSubscriberEmail,
        active: nextActive,
      });

      setRegistrations((previous) =>
        previous.map((item) =>
          item.subscriberId === normalizedSubscriberEmail ||
          normalizeEmail(item.email || "") === normalizedSubscriberEmail
            ? {
                ...item,
                deliveryActive: nextActive,
                unsubscribedAt: nextActive ? null : item.unsubscribedAt || statusChangedAt,
              }
            : item
        )
      );

      setRegistrationStatus({
        type: "success",
        message: nextActive
          ? `Subscriber reactivated (${result.updatedRegistrations} delivery records updated).`
          : `Subscriber unsubscribed (${result.updatedRegistrations} delivery records updated).`,
      });
    } catch (error) {
      console.error("Failed to toggle subscriber status:", error);
      setRegistrationStatus({
        type: "error",
        message: "Could not update subscriber status. Please try again.",
      });
    } finally {
      setTogglingSubscriberEmail("");
    }
  };

  const handleBackfillSubscribers = async () => {
    const approved = window.confirm(
      "Run migration backfill now? This will upsert subscribers and create deterministic delivery records."
    );

    if (!approved) {
      return;
    }

    setIsBackfilling(true);
    setRegistrationStatus({ type: "", message: "" });

    try {
      const result = await backfillSubscribersFromLegacyRegistrations(20000);
      await loadRegistrations();

      setRegistrationStatus({
        type: "success",
        message:
          `Backfill complete. Processed ${result.processed}. ` +
          `Subscribers upserted: ${result.subscribersUpserted}. ` +
          `Deliveries upserted: ${result.deliveriesUpserted}.`,
      });
    } catch (error) {
      console.error("Backfill migration failed:", error);
      setRegistrationStatus({
        type: "error",
        message: "Backfill failed. Please try again.",
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  const associationOptions = useMemo(() => {
    return Array.from(
      new Set(
        registrations
          .filter((registration) => registration.sourceType === "association")
          .map((registration) => registration.sourceName || "")
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [registrations]);

  const roleOptions = useMemo(() => {
    return Array.from(
      new Set(registrations.map((registration) => registration.role || "").filter(Boolean))
    ).sort((left, right) => left.localeCompare(right));
  }, [registrations]);

  const stateOptions = useMemo(() => {
    return Array.from(
      new Set(registrations.map((registration) => registration.state || "").filter(Boolean))
    ).sort((left, right) => left.localeCompare(right));
  }, [registrations]);

  const sourceTypeOptions = useMemo(() => {
    return Array.from(
      new Set(
        registrations
          .map((registration) => registration.sourceType || "")
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [registrations]);

  const sourceSlugOptions = useMemo(() => {
    return Array.from(
      new Set(
        registrations
          .map((registration) => registration.sourceSlug || "")
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [registrations]);

  const labOptions = useMemo(() => {
    const map = new Map();

    labs.forEach((lab) => {
      map.set(lab.id, lab.title || lab.id);
    });

    registrations.forEach((registration) => {
      if (!registration.labId || map.has(registration.labId)) {
        return;
      }
      map.set(registration.labId, registration.labId);
    });

    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [labs, registrations]);

  const filteredRegistrations = useMemo(() => {
    const search = registrationFilters.search.trim().toLowerCase();
    const fromDate = toDateStartOfDay(registrationFilters.dateFrom);
    const toDate = toDateEndOfDay(registrationFilters.dateTo);

    const filtered = registrations.filter((registration) => {
      if (search) {
        const firstName = `${registration.firstName || ""}`.toLowerCase();
        const email = `${registration.email || ""}`.toLowerCase();
        if (!firstName.includes(search) && !email.includes(search)) {
          return false;
        }
      }

      if (
        registrationFilters.association &&
        registration.sourceName !== registrationFilters.association
      ) {
        return false;
      }

      if (registrationFilters.subscriptionStatus === "active" && registration.deliveryActive === false) {
        return false;
      }

      if (
        registrationFilters.subscriptionStatus === "unsubscribed" &&
        registration.deliveryActive !== false
      ) {
        return false;
      }

      if (registrationFilters.state && registration.state !== registrationFilters.state) {
        return false;
      }

      if (registrationFilters.role && registration.role !== registrationFilters.role) {
        return false;
      }

      if (registrationFilters.labId && registration.labId !== registrationFilters.labId) {
        return false;
      }

      if (
        registrationFilters.sourceType &&
        registration.sourceType !== registrationFilters.sourceType
      ) {
        return false;
      }

      if (
        registrationFilters.sourceSlug &&
        registration.sourceSlug !== registrationFilters.sourceSlug
      ) {
        return false;
      }

      const createdAt = toDateValue(registration.createdAt);
      if (fromDate && (!createdAt || createdAt < fromDate)) {
        return false;
      }

      if (toDate && (!createdAt || createdAt > toDate)) {
        return false;
      }

      return true;
    });

    const sorted = [...filtered].sort((left, right) => {
      const leftMs = getRegistrationTimestampMs(left);
      const rightMs = getRegistrationTimestampMs(right);

      if (registrationFilters.sort === "oldest") {
        return leftMs - rightMs || left.email.localeCompare(right.email);
      }

      return rightMs - leftMs || left.email.localeCompare(right.email);
    });

    return sorted;
  }, [registrations, registrationFilters]);

  useEffect(() => {
    const topScroller = topTableScrollRef.current;
    const topScrollerInner = topTableScrollInnerRef.current;
    const bottomScroller = bottomTableScrollRef.current;

    if (!topScroller || !topScrollerInner || !bottomScroller) {
      return undefined;
    }

    const syncWidth = () => {
      topScrollerInner.style.width = `${bottomScroller.scrollWidth}px`;
      topScroller.scrollLeft = bottomScroller.scrollLeft;
    };

    syncWidth();

    const handleTopScroll = () => {
      if (isSyncingTableScrollRef.current) {
        return;
      }

      isSyncingTableScrollRef.current = true;
      bottomScroller.scrollLeft = topScroller.scrollLeft;
      isSyncingTableScrollRef.current = false;
    };

    const handleBottomScroll = () => {
      if (isSyncingTableScrollRef.current) {
        return;
      }

      isSyncingTableScrollRef.current = true;
      topScroller.scrollLeft = bottomScroller.scrollLeft;
      isSyncingTableScrollRef.current = false;
    };

    topScroller.addEventListener("scroll", handleTopScroll);
    bottomScroller.addEventListener("scroll", handleBottomScroll);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        syncWidth();
      });
      resizeObserver.observe(bottomScroller);
    }

    window.addEventListener("resize", syncWidth);

    return () => {
      topScroller.removeEventListener("scroll", handleTopScroll);
      bottomScroller.removeEventListener("scroll", handleBottomScroll);
      window.removeEventListener("resize", syncWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [filteredRegistrations, visibleColumns, selectedRegistrationId, registrationMode]);

  const topAssociations = useMemo(() => {
    return getTopGroups(
      filteredRegistrations,
      (registration) =>
        registration.sourceType === "association"
          ? registration.sourceName || "Association page"
          : "Public / other"
    );
  }, [filteredRegistrations]);

  const topStates = useMemo(() => {
    return getTopGroups(
      filteredRegistrations,
      (registration) => registration.state || "Unknown"
    );
  }, [filteredRegistrations]);

  const topLabs = useMemo(() => {
    return getTopGroups(filteredRegistrations, (registration) =>
      getLabLabel(registration.labId)
    );
  }, [filteredRegistrations, labTitleById]);

  const trend7 = useMemo(() => getTrend(filteredRegistrations, 7), [filteredRegistrations]);
  const trend30 = useMemo(() => getTrend(filteredRegistrations, 30), [filteredRegistrations]);

  const handleExportCsv = () => {
    if (filteredRegistrations.length === 0) {
      setRegistrationStatus({
        type: "error",
        message: "No registrations match the current filters.",
      });
      return;
    }

    const rows = [
      [
        "Registration ID",
        "Registered At",
        "First Name",
        "Email",
        "Role",
        "State",
        "Subscription Active",
        "Association",
        "Source Type",
        "Source Slug",
        "Lab ID",
        "Lab Title",
        "Tags",
        "Notes",
        "Last Confirmation Resent At",
      ],
      ...filteredRegistrations.map((registration) => [
        registration.id,
        formatRegistrationDateTime(registration.createdAt),
        registration.firstName || "",
        registration.email || "",
        registration.role || "",
        registration.state || "",
        registration.deliveryActive === false ? "No" : "Yes",
        registration.sourceName || "",
        registration.sourceType || "",
        registration.sourceSlug || "",
        registration.labId || "",
        getLabLabel(registration.labId),
        parseTags(registration.tags).join(" | "),
        registration.notes || "",
        formatRegistrationDateTime(registration.lastConfirmationResentAt),
      ]),
    ];

    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCsv(rows, `registrants-export-${dateStamp}.csv`);
    setRegistrationStatus({
      type: "success",
      message: "CSV exported.",
    });
  };

  const handleDownloadBulkTemplate = () => {
    downloadCsv([BULK_IMPORT_TEMPLATE_HEADERS], "registrant-bulk-template.csv");
    setBulkImportStatus({
      type: "success",
      message: "Template downloaded.",
    });
  };

  const handleDownloadBulkSample = () => {
    downloadCsv(
      [BULK_IMPORT_TEMPLATE_HEADERS, BULK_IMPORT_SAMPLE_ROW],
      "registrant-bulk-sample.csv"
    );
    setBulkImportStatus({
      type: "success",
      message: "Sample CSV downloaded.",
    });
  };

  const handleBulkImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setBulkImportStatus({
        type: "error",
        message: "Please choose a .csv file.",
      });
      setBulkImportCsvText("");
      setBulkImportFileName("");
      setBulkImportReport(null);
      return;
    }

    try {
      const csvText = await file.text();
      const parsed = parseBulkCsv(csvText);

      setBulkImportCsvText(csvText);
      setBulkImportFileName(file.name);
      setBulkImportReport(null);

      if (parsed.error) {
        setBulkImportStatus({
          type: "error",
          message: parsed.error,
        });
        return;
      }

      if (parsed.missingRequired.length > 0) {
        setBulkImportStatus({
          type: "error",
          message: `Missing required columns: ${parsed.missingRequired.join(", ")}.`,
        });
        return;
      }

      setBulkImportStatus({
        type: "success",
        message: `Loaded ${parsed.rows.length} rows from ${file.name}.`,
      });
    } catch (error) {
      console.error("Failed to read bulk import file:", error);
      setBulkImportStatus({
        type: "error",
        message: "Could not read this CSV file. Please try again.",
      });
    }
  };

  const handleRunBulkImport = async () => {
    setBulkImportStatus({ type: "", message: "" });
    setBulkImportReport(null);

    if (!bulkImportCsvText.trim()) {
      setBulkImportStatus({
        type: "error",
        message: "Upload a CSV file first.",
      });
      return;
    }

    if (!bulkImportLabId) {
      setBulkImportStatus({
        type: "error",
        message: "Choose a target lab for this bulk add.",
      });
      return;
    }

    const parsed = parseBulkCsv(bulkImportCsvText);
    if (parsed.error) {
      setBulkImportStatus({
        type: "error",
        message: parsed.error,
      });
      return;
    }

    if (parsed.missingRequired.length > 0) {
      setBulkImportStatus({
        type: "error",
        message: `Missing required columns: ${parsed.missingRequired.join(", ")}.`,
      });
      return;
    }

    setIsBulkImporting(true);

    try {
      let targetLab = labs.find((lab) => lab.id === bulkImportLabId) || null;
      if (!targetLab) {
        targetLab = await getLabById(bulkImportLabId, { includeUnpublished: true });
      }

      if (!targetLab) {
        setBulkImportStatus({
          type: "error",
          message: "Target lab was not found. Choose a valid lab and try again.",
        });
        return;
      }

      const existingEmailSet = new Set(
        registrations
          .map((registration) => normalizeEmail(registration.email || ""))
          .filter(Boolean)
      );
      const issues = [];
      let importedCount = 0;
      let skippedDuplicateCount = 0;
      let emailSentCount = 0;

      for (let rowIndex = 0; rowIndex < parsed.rows.length; rowIndex += 1) {
        const cells = parsed.rows[rowIndex];
        const rowNumber = rowIndex + 2;
        const isBlank = cells.every((cell) => !`${cell || ""}`.trim());
        if (isBlank) {
          continue;
        }

        const rowData = {};
        parsed.columns.forEach((columnKey, columnIndex) => {
          if (!columnKey) {
            return;
          }

          rowData[columnKey] = `${cells[columnIndex] || ""}`.trim();
        });

        const firstName = `${rowData.firstName || ""}`.trim();
        const email = normalizeEmail(rowData.email || "");
        const role = `${rowData.role || ""}`.trim();
        const state = normalizeStateToAbbreviation(rowData.state || "");
        const missingFields = BULK_IMPORT_REQUIRED_FIELDS.filter(
          (field) => !`${rowData[field] || ""}`.trim()
        );

        if (missingFields.length > 0) {
          issues.push({
            rowNumber,
            firstName,
            email: rowData.email || "",
            status: "Failed",
            reason: `Missing required field(s): ${missingFields.join(", ")}.`,
          });
          continue;
        }

        if (!email) {
          issues.push({
            rowNumber,
            firstName,
            email: rowData.email || "",
            status: "Failed",
            reason: "Invalid email address.",
          });
          continue;
        }

        if (existingEmailSet.has(email)) {
          skippedDuplicateCount += 1;
          issues.push({
            rowNumber,
            firstName,
            email,
            status: "Skipped duplicate",
            reason: "Email already exists. Row skipped.",
          });
          continue;
        }

        const sourceType = rowData.sourceType || BULK_IMPORT_DEFAULT_SOURCE.sourceType;
        const sourceSlug = rowData.sourceSlug || BULK_IMPORT_DEFAULT_SOURCE.sourceSlug;
        const sourceName = rowData.sourceName || BULK_IMPORT_DEFAULT_SOURCE.sourceName;

        try {
          const subscriber = await upsertSubscriber({
            firstName,
            email,
            role,
            state,
            sourceType,
            sourceSlug,
            sourceName,
            active: true,
          });

          const delivery = await upsertLabDeliveryRegistration({
            firstName: subscriber.firstName || firstName,
            email: subscriber.email || email,
            role: subscriber.role || role,
            state: subscriber.state || state,
            labId: targetLab.id,
            sourceType: subscriber.sourceType || sourceType,
            sourceSlug: subscriber.sourceSlug || sourceSlug,
            sourceName: subscriber.sourceName || sourceName,
            requestConfirmation: bulkImportSendEmails,
          });

          importedCount += 1;
          existingEmailSet.add(email);

          if (bulkImportSendEmails) {
            emailSentCount += 1;
            if (!delivery.registrationId) {
              issues.push({
                rowNumber,
                firstName,
                email,
                status: "Imported (email not queued)",
                reason: "Imported successfully, but no target lab registration was created.",
              });
            }
          }
        } catch (error) {
          console.error("Bulk import row failed:", error);
          issues.push({
            rowNumber,
            firstName,
            email,
            status: "Failed",
            reason:
              error?.message && `${error.message}`.length <= 140
                ? error.message
                : "Could not import this row.",
          });
        }
      }

      await loadRegistrations();

      const report = {
        importedCount,
        skippedDuplicateCount,
        emailSentCount,
        issues,
      };
      setBulkImportReport(report);

      const issueCount = issues.length;
      const summary = `Imported ${importedCount} row(s). Skipped duplicates: ${skippedDuplicateCount}. ${
        bulkImportSendEmails
          ? `Confirmation emails queued: ${emailSentCount}.`
          : "Confirmation emails were not sent."
      }`;

      setBulkImportStatus({
        type: issueCount > 0 ? "error" : "success",
        message:
          issueCount > 0
            ? `${summary} ${issueCount} row(s) need review in the issue report below.`
            : summary,
      });
    } catch (error) {
      console.error("Bulk import failed:", error);
      setBulkImportStatus({
        type: "error",
        message:
          "Bulk import failed before finishing. Please review the CSV and try again.",
      });
    } finally {
      setIsBulkImporting(false);
    }
  };

  const handleDownloadBulkIssueReport = () => {
    if (!bulkImportReport || bulkImportReport.issues.length === 0) {
      setBulkImportStatus({
        type: "error",
        message: "No issue report is available yet.",
      });
      return;
    }

    const rows = [
      ["Row", "First Name", "Email", "Status", "Reason"],
      ...bulkImportReport.issues.map((issue) => [
        `${issue.rowNumber}`,
        issue.firstName || "",
        issue.email || "",
        issue.status || "",
        issue.reason || "",
      ]),
    ];

    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCsv(rows, `registrant-bulk-import-issues-${dateStamp}.csv`);
    setBulkImportStatus({
      type: "success",
      message: "Issue report downloaded.",
    });
  };

  const renderRegistrantsGroup = (items) => {
    if (items.length === 0) {
      return <p className="muted-copy">No data for current filters.</p>;
    }

    return (
      <ul className="admin-metric-list">
        {items.map((item) => (
          <li key={item.label}>
            <span>{item.label}</span>
            <strong>{item.count}</strong>
          </li>
        ))}
      </ul>
    );
  };

  const renderTableCell = (registration, columnId) => {
    switch (columnId) {
      case "createdAt":
        return formatRegistrationDateTime(registration.createdAt);
      case "firstName":
        return registration.firstName || "-";
      case "email":
        return registration.email || "-";
      case "role":
        return registration.role || "-";
      case "state":
        return registration.state || "-";
      case "subscriberActive":
        return registration.deliveryActive === false ? "Unsubscribed" : "Active";
      case "unsubscribedAt":
        return registration.unsubscribedAt
          ? formatRegistrationDateTime(registration.unsubscribedAt)
          : "-";
      case "association":
        return registration.sourceName || "-";
      case "sourceType":
        return registration.sourceType || "-";
      case "sourceSlug":
        return registration.sourceSlug || "-";
      case "lab":
        return getLabLabel(registration.labId);
      case "tags":
        return parseTags(registration.tags).join(", ") || "-";
      case "notes":
        return notePreview(registration.notes);
      default:
        return "-";
    }
  };

  if (!isUnlocked) {
    return (
      <>
        <SiteHeader showRegisterButton={false} />
        <main className="section">
          <div className="container narrow">
            <article className="card admin-gate">
              <h1>Admin access</h1>
              <p>Enter the passphrase to manage lab content.</p>

              <form className="form" onSubmit={handleUnlock}>
                <label className="form-field" htmlFor="adminPassphrase">
                  <span>Passphrase</span>
                  <input
                    id="adminPassphrase"
                    type="password"
                    value={passphrase}
                    onChange={(event) => setPassphrase(event.target.value)}
                    required
                  />
                </label>

                <button className="btn btn-primary" type="submit">
                  Unlock
                </button>

                {unlockError ? <p className="status-message error">{unlockError}</p> : null}
              </form>
            </article>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isPublicPreview = Boolean(formData.id && formData.status === "published");
  const isAnyRegistrationActionBusy = Boolean(
    isSavingRegistration ||
      resendingRegistrationId ||
      deletingRegistrationId ||
      togglingSubscriberEmail
  );

  return (
    <>
      <SiteHeader showRegisterButton={false} />
      <main className="section">
        <div className="container">
          <div className="admin-layout">
            <section>
              <div className="admin-header-row">
                <div>
                  <h1>Lab Content Admin</h1>
                  <p className="admin-subtitle">
                    Path: <code>{ADMIN_ROUTE_PATH}</code>
                  </p>
                </div>
                <button className="btn btn-ghost" type="button" onClick={handleStartNew}>
                  Start new lab
                </button>
              </div>

              <form className="form card" onSubmit={handleSaveLab} noValidate>
                <div className="form-field">
                  <span>Lab ID / slug</span>
                  <div className="inline-row">
                    <input
                      name="id"
                      type="text"
                      value={formData.id}
                      onChange={handleLabFormChange}
                      onBlur={handleIdBlur}
                      placeholder="example: ai-lab-march-2026"
                      required
                    />
                    <button className="btn btn-outline" type="button" onClick={handleGenerateId}>
                      Use title
                    </button>
                  </div>
                </div>

                <div className="form-grid">
                  <label className="form-field" htmlFor="adminTitle">
                    <span>Title</span>
                    <input
                      id="adminTitle"
                      name="title"
                      type="text"
                      value={formData.title}
                      onChange={handleLabFormChange}
                      required
                    />
                  </label>

                  <label className="form-field" htmlFor="adminDateTime">
                    <span>Date and time (CT recommended)</span>
                    <input
                      id="adminDateTime"
                      name="dateTimeLocal"
                      type="datetime-local"
                      value={formData.dateTimeLocal}
                      onChange={handleLabFormChange}
                      required
                    />
                  </label>

                  <label className="form-field" htmlFor="adminDuration">
                    <span>Duration (minutes)</span>
                    <input
                      id="adminDuration"
                      name="durationMinutes"
                      type="number"
                      min="15"
                      step="5"
                      value={formData.durationMinutes}
                      onChange={handleLabFormChange}
                      required
                    />
                  </label>

                  <label className="form-field" htmlFor="adminStatus">
                    <span>Status</span>
                    <select
                      id="adminStatus"
                      name="status"
                      value={formData.status}
                      onChange={handleLabFormChange}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>

                  <label className="form-field" htmlFor="adminYouTubeId">
                    <span>YouTube ID (optional)</span>
                    <input
                      id="adminYouTubeId"
                      name="youtubeId"
                      type="text"
                      value={formData.youtubeId}
                      onChange={handleLabFormChange}
                    />
                  </label>
                </div>

                <label className="form-field" htmlFor="adminShortDescription">
                  <span>Short description</span>
                  <textarea
                    id="adminShortDescription"
                    name="shortDescription"
                    rows="2"
                    value={formData.shortDescription}
                    onChange={handleLabFormChange}
                    required
                  />
                </label>

                <label className="form-field" htmlFor="adminLongDescription">
                  <span>Long description</span>
                  <textarea
                    id="adminLongDescription"
                    name="longDescription"
                    rows="6"
                    value={formData.longDescription}
                    onChange={handleLabFormChange}
                    required
                  />
                </label>

                  <label className="form-field" htmlFor="adminZoomLink">
                    <span>Zoom link</span>
                    <input
                      id="adminZoomLink"
                      name="zoomLink"
                      type="text"
                      value={formData.zoomLink}
                      onChange={handleLabFormChange}
                      placeholder="https://us02web.zoom.us/j/123456789"
                      required
                    />
                  </label>

                <label className="form-field" htmlFor="adminResources">
                  <span>Resources (one per line as Label|URL)</span>
                  <textarea
                    id="adminResources"
                    name="resourcesText"
                    rows="4"
                    value={formData.resourcesText}
                    onChange={handleLabFormChange}
                    placeholder="Slide deck|https://example.com/slides"
                  />
                </label>

                <section className="admin-content-builder">
                  <div className="admin-builder-header">
                    <div>
                      <h3>Polls</h3>
                      <p className="muted-copy">
                        Add as many discussion polls as you want for this lab.
                      </p>
                    </div>
                    <button className="btn btn-outline" type="button" onClick={handleAddPoll}>
                      Add poll
                    </button>
                  </div>

                  {(formData.polls || []).length === 0 ? (
                    <p className="muted-copy">No polls yet for this lab.</p>
                  ) : null}

                  <div className="admin-builder-list">
                    {(formData.polls || []).map((poll, pollIndex) => (
                      <article className="card admin-builder-item" key={poll.id || `poll-${pollIndex}`}>
                        <div className="admin-builder-item-head">
                          <strong>Poll {pollIndex + 1}</strong>
                          <div className="admin-builder-inline-actions">
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => handleMovePoll(pollIndex, -1)}
                              disabled={pollIndex === 0}
                            >
                              Up
                            </button>
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => handleMovePoll(pollIndex, 1)}
                              disabled={pollIndex === (formData.polls || []).length - 1}
                            >
                              Down
                            </button>
                            <button
                              className="btn btn-outline admin-delete-btn"
                              type="button"
                              onClick={() => handleRemovePoll(pollIndex)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        <label className="form-field">
                          <span>Poll question</span>
                          <textarea
                            rows="3"
                            value={poll.question || ""}
                            onChange={(event) => handlePollChange(pollIndex, event.target.value)}
                            placeholder="Example: What is one workflow you want to improve this month?"
                          />
                        </label>
                      </article>
                    ))}
                  </div>
                </section>

                <section className="admin-content-builder">
                  <div className="admin-builder-header">
                    <div>
                      <h3>Activities</h3>
                      <p className="muted-copy">
                        Build step-by-step activities with links and optional AI prompts.
                      </p>
                    </div>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={handleAddActivity}
                    >
                      Add activity
                    </button>
                  </div>

                  {(formData.activities || []).length === 0 ? (
                    <p className="muted-copy">No activities yet for this lab.</p>
                  ) : null}

                  <div className="admin-builder-list">
                    {(formData.activities || []).map((activity, activityIndex) => (
                      <article
                        className="card admin-builder-item admin-activity-item"
                        key={activity.id || `activity-${activityIndex}`}
                      >
                        <div className="admin-builder-item-head">
                          <strong>Activity {activityIndex + 1}</strong>
                          <div className="admin-builder-inline-actions">
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => handleMoveActivity(activityIndex, -1)}
                              disabled={activityIndex === 0}
                            >
                              Up
                            </button>
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => handleMoveActivity(activityIndex, 1)}
                              disabled={activityIndex === (formData.activities || []).length - 1}
                            >
                              Down
                            </button>
                            <button
                              className="btn btn-outline admin-delete-btn"
                              type="button"
                              onClick={() => handleRemoveActivity(activityIndex)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        <label className="form-field">
                          <span>Activity title</span>
                          <input
                            type="text"
                            value={activity.title || ""}
                            onChange={(event) =>
                              handleActivityTitleChange(activityIndex, event.target.value)
                            }
                            placeholder="Example: Draft your principal weekly update with AI"
                          />
                        </label>

                        <section className="admin-nested-section">
                          <div className="admin-builder-item-head">
                            <strong>Links for this activity</strong>
                            <button
                              className="btn btn-outline"
                              type="button"
                              onClick={() => handleAddActivityLink(activityIndex)}
                            >
                              Add link
                            </button>
                          </div>

                          {(activity.links || []).length === 0 ? (
                            <p className="muted-copy">No links yet.</p>
                          ) : null}

                          <div className="admin-builder-list">
                            {(activity.links || []).map((link, linkIndex) => (
                              <div className="admin-link-row" key={`${activity.id}-link-${linkIndex}`}>
                                <input
                                  type="text"
                                  value={link.label || ""}
                                  onChange={(event) =>
                                    handleActivityLinkChange(
                                      activityIndex,
                                      linkIndex,
                                      "label",
                                      event.target.value
                                    )
                                  }
                                  placeholder="Link label"
                                />
                                <input
                                  type="text"
                                  value={link.url || ""}
                                  onChange={(event) =>
                                    handleActivityLinkChange(
                                      activityIndex,
                                      linkIndex,
                                      "url",
                                      event.target.value
                                    )
                                  }
                                  placeholder="https://example.com"
                                />
                                <button
                                  className="btn btn-outline admin-delete-btn"
                                  type="button"
                                  onClick={() => handleRemoveActivityLink(activityIndex, linkIndex)}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className="admin-nested-section">
                          <div className="admin-builder-item-head">
                            <strong>Steps</strong>
                            <button
                              className="btn btn-outline"
                              type="button"
                              onClick={() => handleAddActivityStep(activityIndex)}
                            >
                              Add step
                            </button>
                          </div>

                          {(activity.steps || []).length === 0 ? (
                            <p className="muted-copy">No steps yet.</p>
                          ) : null}

                          <div className="admin-builder-list">
                            {(activity.steps || []).map((step, stepIndex) => (
                              <article
                                className="card admin-builder-item admin-step-item"
                                key={step.id || `${activity.id}-step-${stepIndex}`}
                              >
                                <div className="admin-builder-item-head">
                                  <strong>Step {stepIndex + 1}</strong>
                                  <div className="admin-builder-inline-actions">
                                    <button
                                      className="btn btn-ghost"
                                      type="button"
                                      onClick={() =>
                                        handleMoveActivityStep(activityIndex, stepIndex, -1)
                                      }
                                      disabled={stepIndex === 0}
                                    >
                                      Up
                                    </button>
                                    <button
                                      className="btn btn-ghost"
                                      type="button"
                                      onClick={() =>
                                        handleMoveActivityStep(activityIndex, stepIndex, 1)
                                      }
                                      disabled={stepIndex === (activity.steps || []).length - 1}
                                    >
                                      Down
                                    </button>
                                    <button
                                      className="btn btn-outline admin-delete-btn"
                                      type="button"
                                      onClick={() =>
                                        handleRemoveActivityStep(activityIndex, stepIndex)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>

                                <label className="form-field">
                                  <span>Directions</span>
                                  <textarea
                                    rows="2"
                                    value={step.text || ""}
                                    onChange={(event) =>
                                      handleActivityStepChange(
                                        activityIndex,
                                        stepIndex,
                                        "text",
                                        event.target.value
                                      )
                                    }
                                    placeholder="Step instructions"
                                  />
                                </label>

                                <label className="form-field">
                                  <span>AI prompt (optional)</span>
                                  <textarea
                                    rows="3"
                                    value={step.prompt || ""}
                                    onChange={(event) =>
                                      handleActivityStepChange(
                                        activityIndex,
                                        stepIndex,
                                        "prompt",
                                        event.target.value
                                      )
                                    }
                                    placeholder="Prompt learners can copy during this step"
                                  />
                                </label>
                              </article>
                            ))}
                          </div>
                        </section>
                      </article>
                    ))}
                  </div>
                </section>

                <div className="action-row">
                  <button className="btn btn-primary" type="submit" disabled={isSavingLab}>
                    {isSavingLab ? "Saving..." : "Save lab"}
                  </button>

                  {isPublicPreview ? (
                    <Link className="btn btn-ghost" to={`/lab/${formData.id}`}>
                      Open public page
                    </Link>
                  ) : (
                    <button className="btn btn-ghost" type="button" disabled>
                      Publish to preview
                    </button>
                  )}

                  {activeLabId ? (
                    <button className="btn btn-outline" type="button" onClick={handleArchiveActive}>
                      Archive lab
                    </button>
                  ) : null}
                </div>

                {saveStatus.message ? (
                  <p className={`status-message ${saveStatus.type}`}>{saveStatus.message}</p>
                ) : null}
              </form>
            </section>

            <aside className="card admin-sidebar">
              <h2>Existing labs</h2>
              {isLoadingLabs ? <p>Loading labs...</p> : null}
              {labsLoadError ? <p className="status-message error">{labsLoadError}</p> : null}
              {!isLoadingLabs && !labsLoadError && labs.length === 0 ? (
                <p>No labs yet. Create your first session with the form.</p>
              ) : null}

              <div className="admin-lab-list">
                {labs.map((lab) => (
                  <article key={lab.id} className="admin-lab-item">
                    <div className="action-row">
                      <span className={`status-badge ${lab.status || "published"}`}>
                        {statusLabel(lab.status)}
                      </span>
                    </div>
                    <p className="eyebrow">{formatLabDate(lab.dateTime)}</p>
                    <h3>{lab.title}</h3>
                    <p className="lab-time">
                      {formatLabTimeRange(lab.dateTime, lab.durationMinutes)}
                    </p>
                    <div className="action-row">
                      <button
                        className="btn btn-outline"
                        type="button"
                        onClick={() => handleEditLab(lab)}
                      >
                        Edit
                      </button>

                      <button
                        className="btn btn-outline"
                        type="button"
                        onClick={() => handleDuplicateLab(lab)}
                      >
                        Duplicate
                      </button>

                      {lab.status === "published" ? (
                        <Link className="btn btn-ghost" to={`/lab/${lab.id}`}>
                          Open
                        </Link>
                      ) : (
                        <button className="btn btn-ghost" type="button" disabled>
                          Hidden
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </aside>
          </div>

          <section className="admin-registrants-section">
            <div className="admin-header-row">
              <div>
                <h2>Registrant Console</h2>
                <p className="admin-subtitle">
                  Track where registrants come from, edit records, and resend confirmations.
                </p>
              </div>
              <div className="action-row admin-registrant-actions">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={loadRegistrations}
                  disabled={isLoadingRegistrations}
                >
                  {isLoadingRegistrations ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleBackfillSubscribers}
                  disabled={isBackfilling}
                >
                  {isBackfilling ? "Backfilling..." : "Run backfill"}
                </button>
                <button className="btn btn-primary" type="button" onClick={handleExportCsv}>
                  Export CSV
                </button>
              </div>
            </div>

            <div className="admin-registrant-summary">
              <p>
                Showing <strong>{filteredRegistrations.length}</strong> of{" "}
                <strong>{registrations.length}</strong> registrants.
              </p>
            </div>

            <section className="card admin-bulk-card">
              <div className="admin-bulk-header">
                <h3>Bulk add registrants (CSV)</h3>
                <p className="muted-copy">
                  Required columns: <code>firstName</code>, <code>email</code>,{" "}
                  <code>role</code>, <code>state</code>. Duplicates are skipped.
                </p>
              </div>

              <div className="admin-bulk-grid">
                <label className="form-field" htmlFor="adminBulkImportCsv">
                  <span>CSV file</span>
                  <input
                    id="adminBulkImportCsv"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleBulkImportFileChange}
                    disabled={isBulkImporting}
                  />
                </label>

                <label className="form-field" htmlFor="adminBulkImportLab">
                  <span>Target lab</span>
                  <select
                    id="adminBulkImportLab"
                    value={bulkImportLabId}
                    onChange={(event) => setBulkImportLabId(event.target.value)}
                    disabled={isBulkImporting}
                  >
                    <option value="">Select a lab</option>
                    {labs.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.title || lab.id} ({statusLabel(lab.status)})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-bulk-checkbox" htmlFor="adminBulkSendEmails">
                  <input
                    id="adminBulkSendEmails"
                    type="checkbox"
                    checked={bulkImportSendEmails}
                    onChange={(event) => setBulkImportSendEmails(event.target.checked)}
                    disabled={isBulkImporting}
                  />
                  <span>Send confirmation emails during import</span>
                </label>
              </div>

              <div className="action-row admin-bulk-actions">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleDownloadBulkTemplate}
                  disabled={isBulkImporting}
                >
                  Download template
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={handleDownloadBulkSample}
                  disabled={isBulkImporting}
                >
                  Download sample
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleRunBulkImport}
                  disabled={isBulkImporting || !bulkImportCsvText.trim()}
                >
                  {isBulkImporting ? "Importing..." : "Run bulk add"}
                </button>
                {bulkImportReport?.issues?.length ? (
                  <button
                    className="btn btn-subtle"
                    type="button"
                    onClick={handleDownloadBulkIssueReport}
                    disabled={isBulkImporting}
                  >
                    Download issue report
                  </button>
                ) : null}
              </div>

              {bulkImportFileName ? (
                <p className="admin-bulk-file-meta">
                  Loaded file: <strong>{bulkImportFileName}</strong>
                </p>
              ) : null}

              {bulkImportStatus.message ? (
                <p className={`status-message ${bulkImportStatus.type}`}>
                  {bulkImportStatus.message}
                </p>
              ) : null}

              {bulkImportReport ? (
                <div className="admin-bulk-summary">
                  <p>
                    Imported: <strong>{bulkImportReport.importedCount}</strong>
                  </p>
                  <p>
                    Skipped duplicates:{" "}
                    <strong>{bulkImportReport.skippedDuplicateCount}</strong>
                  </p>
                  <p>
                    Emails queued: <strong>{bulkImportReport.emailSentCount}</strong>
                  </p>
                  <p>
                    Issues: <strong>{bulkImportReport.issues.length}</strong>
                  </p>
                </div>
              ) : null}

              {bulkImportReport?.issues?.length ? (
                <div className="admin-bulk-issues-wrap">
                  <h4>Issue report</h4>
                  <div className="admin-bulk-issues-table-wrap">
                    <table className="admin-bulk-issues-table">
                      <thead>
                        <tr>
                          <th scope="col">Row</th>
                          <th scope="col">First name</th>
                          <th scope="col">Email</th>
                          <th scope="col">Status</th>
                          <th scope="col">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkImportReport.issues.map((issue) => (
                          <tr key={`${issue.rowNumber}-${issue.email}-${issue.status}`}>
                            <td>{issue.rowNumber}</td>
                            <td>{issue.firstName || "-"}</td>
                            <td>{issue.email || "-"}</td>
                            <td>{issue.status || "-"}</td>
                            <td>{issue.reason || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </section>

            <div className="admin-registrant-metrics">
              <article className="card admin-metric-card">
                <p className="eyebrow">Total registrants</p>
                <h3>{filteredRegistrations.length}</h3>
                <p className="muted-copy">Based on current filters.</p>
              </article>

              <article className="card admin-metric-card">
                <p className="eyebrow">Last 7 days</p>
                <h3>{trend7.current}</h3>
                <p className="muted-copy">{summarizeTrendDelta(trend7)}</p>
              </article>

              <article className="card admin-metric-card">
                <p className="eyebrow">Last 30 days</p>
                <h3>{trend30.current}</h3>
                <p className="muted-copy">{summarizeTrendDelta(trend30)}</p>
              </article>

              <article className="card admin-metric-card">
                <p className="eyebrow">Top associations</p>
                {renderRegistrantsGroup(topAssociations)}
              </article>

              <article className="card admin-metric-card">
                <p className="eyebrow">Top states</p>
                {renderRegistrantsGroup(topStates)}
              </article>

              <article className="card admin-metric-card">
                <p className="eyebrow">Top labs</p>
                {renderRegistrantsGroup(topLabs)}
              </article>
            </div>

            <section className="card admin-filters-card">
              <div className="admin-filters-header">
                <h3>Filters</h3>
                <button className="btn btn-subtle" type="button" onClick={handleClearRegistrationFilters}>
                  Clear filters
                </button>
              </div>

              <div className="admin-filter-grid">
                <label className="form-field" htmlFor="adminFilterSearch">
                  <span>Email or first name</span>
                  <input
                    id="adminFilterSearch"
                    name="search"
                    type="text"
                    value={registrationFilters.search}
                    onChange={handleRegistrationFilterChange}
                    placeholder="Search email or name"
                  />
                </label>

                <label className="form-field" htmlFor="adminFilterDateFrom">
                  <span>Date from</span>
                  <input
                    id="adminFilterDateFrom"
                    name="dateFrom"
                    type="date"
                    value={registrationFilters.dateFrom}
                    onChange={handleRegistrationFilterChange}
                  />
                </label>

                <label className="form-field" htmlFor="adminFilterDateTo">
                  <span>Date to</span>
                  <input
                    id="adminFilterDateTo"
                    name="dateTo"
                    type="date"
                    value={registrationFilters.dateTo}
                    onChange={handleRegistrationFilterChange}
                  />
                </label>

                <label className="form-field" htmlFor="adminFilterAssociation">
                  <span>Association</span>
                  <select
                    id="adminFilterAssociation"
                    name="association"
                    value={registrationFilters.association}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {associationOptions.map((association) => (
                      <option key={association} value={association}>
                        {association}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterSubscriptionStatus">
                  <span>Subscription</span>
                  <select
                    id="adminFilterSubscriptionStatus"
                    name="subscriptionStatus"
                    value={registrationFilters.subscriptionStatus}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterState">
                  <span>State</span>
                  <select
                    id="adminFilterState"
                    name="state"
                    value={registrationFilters.state}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {stateOptions.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterRole">
                  <span>Role</span>
                  <select
                    id="adminFilterRole"
                    name="role"
                    value={registrationFilters.role}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterLab">
                  <span>Lab</span>
                  <select
                    id="adminFilterLab"
                    name="labId"
                    value={registrationFilters.labId}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {labOptions.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterSourceType">
                  <span>Source type</span>
                  <select
                    id="adminFilterSourceType"
                    name="sourceType"
                    value={registrationFilters.sourceType}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {sourceTypeOptions.map((sourceType) => (
                      <option key={sourceType} value={sourceType}>
                        {sourceType}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterSourceSlug">
                  <span>Page/source slug</span>
                  <select
                    id="adminFilterSourceSlug"
                    name="sourceSlug"
                    value={registrationFilters.sourceSlug}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="">All</option>
                    {sourceSlugOptions.map((sourceSlug) => (
                      <option key={sourceSlug} value={sourceSlug}>
                        {sourceSlug}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field" htmlFor="adminFilterSort">
                  <span>Sort</span>
                  <select
                    id="adminFilterSort"
                    name="sort"
                    value={registrationFilters.sort}
                    onChange={handleRegistrationFilterChange}
                  >
                    <option value="newest">Newest to oldest</option>
                    <option value="oldest">Oldest to newest</option>
                  </select>
                </label>
              </div>

              <details className="admin-column-picker">
                <summary>Columns</summary>
                <div className="admin-column-grid">
                  {REGISTRATION_COLUMNS.map((column) => (
                    <label key={column.id} className="admin-column-option">
                      <input
                        type="checkbox"
                        checked={Boolean(visibleColumns[column.id])}
                        onChange={() => handleToggleColumn(column.id)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </details>

              {registrationStatus.message ? (
                <p className={`status-message ${registrationStatus.type}`}>
                  {registrationStatus.message}
                </p>
              ) : null}
            </section>

            {isLoadingRegistrations ? <p>Loading registrants...</p> : null}
            {registrationsLoadError ? (
              <p className="status-message error">{registrationsLoadError}</p>
            ) : null}

            {!isLoadingRegistrations && !registrationsLoadError ? (
              <div
                className={`admin-registrants-layout${
                  selectedRegistration ? "" : " admin-registrants-layout--table-only"
                }`}
              >
                <article className="card admin-table-card">
                  {filteredRegistrations.length === 0 ? (
                    <p>No registrants match the current filters.</p>
                  ) : (
                    <>
                      <div className="admin-table-top-scroll" ref={topTableScrollRef}>
                        <div ref={topTableScrollInnerRef} />
                      </div>
                      <div className="admin-table-wrap" ref={bottomTableScrollRef}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            {REGISTRATION_COLUMNS.map((column) =>
                              visibleColumns[column.id] ? (
                                <th key={column.id} scope="col">
                                  {column.label}
                                </th>
                              ) : null
                            )}
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRegistrations.map((registration) => (
                            <tr
                              key={registration.id}
                              className={
                                selectedRegistrationId === registration.id
                                  ? "admin-table-row-selected"
                                  : ""
                              }
                            >
                              {REGISTRATION_COLUMNS.map((column) =>
                                visibleColumns[column.id] ? (
                                  <td key={`${registration.id}-${column.id}`}>
                                    {renderTableCell(registration, column.id)}
                                  </td>
                                ) : null
                              )}
                              <td>
                                <div className="admin-table-actions">
                                  <button
                                    className="btn btn-subtle"
                                    type="button"
                                    onClick={() => openRegistration(registration, "view")}
                                  >
                                    View
                                  </button>
                                  <button
                                    className="btn btn-outline"
                                    type="button"
                                    onClick={() => openRegistration(registration, "edit")}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-outline"
                                    type="button"
                                    onClick={() => handleToggleSubscriberActive(registration)}
                                    disabled={
                                      isAnyRegistrationActionBusy ||
                                      togglingSubscriberEmail ===
                                        (registration.subscriberId ||
                                          normalizeEmail(registration.email || ""))
                                    }
                                  >
                                    {togglingSubscriberEmail ===
                                    (registration.subscriberId ||
                                      normalizeEmail(registration.email || ""))
                                      ? "Updating..."
                                      : registration.deliveryActive === false
                                      ? "Reactivate"
                                      : "Unsubscribe"}
                                  </button>
                                  <button
                                    className="btn btn-ghost"
                                    type="button"
                                    onClick={() => handleResendConfirmation(registration)}
                                    disabled={
                                      isAnyRegistrationActionBusy ||
                                      resendingRegistrationId === registration.id
                                    }
                                  >
                                    {resendingRegistrationId === registration.id
                                      ? "Resending..."
                                      : "Resend email"}
                                  </button>
                                  <button
                                    className="btn btn-outline admin-delete-btn"
                                    type="button"
                                    onClick={() => handleDeleteRegistration(registration)}
                                    disabled={
                                      isAnyRegistrationActionBusy ||
                                      deletingRegistrationId === registration.id
                                    }
                                  >
                                    {deletingRegistrationId === registration.id
                                      ? "Deleting..."
                                      : "Delete"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </>
                  )}
                </article>

                {selectedRegistration ? (
                  <aside className="card admin-registration-panel">
                    {registrationMode === "edit" && registrationEditor ? (
                    <form className="form" onSubmit={handleSaveRegistration}>
                      <div className="admin-panel-header">
                        <h3>Edit registrant</h3>
                        <button
                          className="btn btn-subtle"
                          type="button"
                          onClick={() => setRegistrationMode("view")}
                          disabled={isSavingRegistration}
                        >
                          Cancel
                        </button>
                      </div>

                      <label className="form-field" htmlFor="adminRegistrationFirstName">
                        <span>First name</span>
                        <input
                          id="adminRegistrationFirstName"
                          name="firstName"
                          type="text"
                          value={registrationEditor.firstName}
                          onChange={handleRegistrationEditorChange}
                          required
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationEmail">
                        <span>Email</span>
                        <input
                          id="adminRegistrationEmail"
                          name="email"
                          type="email"
                          value={registrationEditor.email}
                          onChange={handleRegistrationEditorChange}
                          required
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationRole">
                        <span>Role</span>
                        <input
                          id="adminRegistrationRole"
                          name="role"
                          type="text"
                          value={registrationEditor.role}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationState">
                        <span>State</span>
                        <input
                          id="adminRegistrationState"
                          name="state"
                          type="text"
                          maxLength="2"
                          value={registrationEditor.state}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationLab">
                        <span>Lab</span>
                        <select
                          id="adminRegistrationLab"
                          name="labId"
                          value={registrationEditor.labId}
                          onChange={handleRegistrationEditorChange}
                        >
                          <option value="">General monthly lab</option>
                          {labOptions.map((lab) => (
                            <option key={lab.id} value={lab.id}>
                              {lab.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationSourceType">
                        <span>Source type</span>
                        <input
                          id="adminRegistrationSourceType"
                          name="sourceType"
                          type="text"
                          value={registrationEditor.sourceType}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationSourceName">
                        <span>Association / source name</span>
                        <input
                          id="adminRegistrationSourceName"
                          name="sourceName"
                          type="text"
                          value={registrationEditor.sourceName}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationSourceSlug">
                        <span>Source slug</span>
                        <input
                          id="adminRegistrationSourceSlug"
                          name="sourceSlug"
                          type="text"
                          value={registrationEditor.sourceSlug}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationTags">
                        <span>Tags (comma-separated)</span>
                        <input
                          id="adminRegistrationTags"
                          name="tagsText"
                          type="text"
                          value={registrationEditor.tagsText}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <label className="form-field" htmlFor="adminRegistrationNotes">
                        <span>Notes</span>
                        <textarea
                          id="adminRegistrationNotes"
                          name="notes"
                          rows="5"
                          value={registrationEditor.notes}
                          onChange={handleRegistrationEditorChange}
                        />
                      </label>

                      <div className="action-row">
                        <button
                          className="btn btn-primary"
                          type="submit"
                          disabled={isSavingRegistration}
                        >
                          {isSavingRegistration ? "Saving..." : "Save changes"}
                        </button>
                        <button
                          className="btn btn-ghost"
                          type="button"
                          onClick={closeRegistrationPanel}
                          disabled={isSavingRegistration}
                        >
                          Close
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="admin-registration-view">
                      <div className="admin-panel-header">
                        <h3>Registrant details</h3>
                        <div className="action-row admin-registrant-actions">
                          <button
                            className="btn btn-outline"
                            type="button"
                            onClick={() => openRegistration(selectedRegistration, "edit")}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-subtle"
                            type="button"
                            onClick={closeRegistrationPanel}
                          >
                            Close
                          </button>
                        </div>
                      </div>

                      <p>
                        <strong>Registered:</strong>{" "}
                        {formatRegistrationDateTime(selectedRegistration.createdAt)}
                      </p>
                      <p>
                        <strong>First name:</strong> {selectedRegistration.firstName || "-"}
                      </p>
                      <p>
                        <strong>Email:</strong> {selectedRegistration.email || "-"}
                      </p>
                      <p>
                        <strong>Role:</strong> {selectedRegistration.role || "-"}
                      </p>
                      <p>
                        <strong>State:</strong> {selectedRegistration.state || "-"}
                      </p>
                      <p>
                        <strong>Subscription:</strong>{" "}
                        {selectedRegistration.deliveryActive === false
                          ? "Unsubscribed"
                          : "Active"}
                      </p>
                      <p>
                        <strong>Unsubscribed at:</strong>{" "}
                        {selectedRegistration.unsubscribedAt
                          ? formatRegistrationDateTime(selectedRegistration.unsubscribedAt)
                          : "-"}
                      </p>
                      <p>
                        <strong>Lab:</strong> {getLabLabel(selectedRegistration.labId)}
                      </p>
                      <p>
                        <strong>Association / source:</strong>{" "}
                        {selectedRegistration.sourceName || "-"}
                      </p>
                      <p>
                        <strong>Source type:</strong> {selectedRegistration.sourceType || "-"}
                      </p>
                      <p>
                        <strong>Source slug:</strong> {selectedRegistration.sourceSlug || "-"}
                      </p>
                      <p>
                        <strong>Tags:</strong>{" "}
                        {parseTags(selectedRegistration.tags).join(", ") || "-"}
                      </p>
                      <p>
                        <strong>Notes:</strong>{" "}
                        {selectedRegistration.notes ? selectedRegistration.notes : "-"}
                      </p>
                      <p>
                        <strong>Last confirmation resent:</strong>{" "}
                        {formatRegistrationDateTime(
                          selectedRegistration.lastConfirmationResentAt
                        )}
                      </p>

                      <div className="action-row">
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={() => handleToggleSubscriberActive(selectedRegistration)}
                          disabled={
                            isAnyRegistrationActionBusy ||
                            togglingSubscriberEmail ===
                              (selectedRegistration.subscriberId ||
                                normalizeEmail(selectedRegistration.email || ""))
                          }
                        >
                          {selectedRegistration.deliveryActive === false
                            ? "Reactivate subscriber"
                            : "Unsubscribe subscriber"}
                        </button>
                      </div>
                    </div>
                  )}
                  </aside>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default AdminPage;
